/**
 * Gets an event's play world ready while the Mapbox map shows. The world is the square around every course of the
 * event (`worldArea`). A course whose world the server has built (`route.world_url`, docs/features/play-world-map.md
 * §2) loads that pack, about a megabyte or two; the server builds each course's pack over all of the event's courses.
 * The server keeps serving a course's last pack while it builds the next, so a rebuilt world swaps in when a later
 * payload brings the new `world_url`: the world on screen stays until the new pack has loaded. Otherwise the browser
 * builds it: the area and its terrain read from the open datasets (15–20 s), only on the dev server, whose `/cog/*`
 * pass-throughs reach them. Elsewhere a course still without a pack waits while the server builds it (`world_status`
 * "building"). The terrain generator and the world's chunk load only when a world is needed, so the map pays nothing
 * for them.
 */

import { useEffect, useMemo, useState } from 'react'
import type { GeneratedTerrain } from '@/lib/terrain-generator/generate'
import { MAX_SIDE_KM, validateBox } from '@/lib/terrain-generator/grid'
import { parseGpx, worldArea, type GpxRoute } from '@/lib/terrain-generator/route'
import { fetchWorldPack } from '@/lib/terrain-generator/worldPack'
import { routeGpxCacheMode, routeGpxUrlFor, routeWorldUrlFor } from '@/lib/map/routeGpx'
import type { CourseMapProps } from './CourseMap'
import type { DrawnCourse } from './courses'

/** One of an event's several courses in the world: its line, in its colour. */
export interface WorldCourse {
  key: string
  color: string
  /** Where it stacks, 0 at the bottom (DrawnCourse's). */
  order: number
  gpx: GpxRoute
}

export type CourseWorld =
  | { status: 'off' }
  | { status: 'building'; share: number | null }
  /** `courses`: every course of an event with several, in order (null for one course, which is `gpx`). */
  | { status: 'ready'; terrain: GeneratedTerrain; gpx: GpxRoute; courses: WorldCourse[] | null }
  | { status: 'unavailable'; reason: string }

function supportsWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas')
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
  } catch {
    return false
  }
}

/** Only the dev server passes the datasets through (`/cog/*`, vite.config.ts), so only there can the browser build. */
function browserCanBuild(): boolean {
  return import.meta.env.DEV
}

async function fetchCourse(url: string, signal: AbortSignal): Promise<GpxRoute> {
  const response = await fetch(url, { cache: routeGpxCacheMode(url), signal })
  if (!response.ok) throw new Error(`Course fetch ${response.status}`)
  return parseGpx(await response.text())
}

export function useCourseWorld({
  eventId,
  route,
  courses,
  enabled,
}: Pick<CourseMapProps, 'eventId' | 'route'> & { courses: DrawnCourse[] | null; enabled: boolean }): CourseWorld {
  const gpxUrl = enabled ? routeGpxUrlFor({ id: eventId, route }) : null
  const packUrl = enabled ? routeWorldUrlFor({ id: eventId, route }) : null
  // The other courses' lines, by key (the primary's is `gpxUrl`).
  const others = useMemo(
    () =>
      enabled && courses
        ? courses.map((course) => ({ key: course.key, color: course.color, order: course.order, url: routeGpxUrlFor({ id: eventId, route: course.route }) }))
        : null,
    [courses, enabled, eventId],
  )
  const othersKey = others?.map((course) => `${course.key}=${course.url ?? ''}:${course.color}:${course.order}`).join(',') ?? ''
  // How the server's build of a course without a pack is going. With a pack, a build under way changes nothing until
  // the pack's URL does.
  const serverStatus = gpxUrl && !packUrl ? (route?.world_status ?? null) : null
  const key = `${gpxUrl ?? ''}|${packUrl ?? ''}|${serverStatus ?? ''}|${othersKey}`
  // `ready` is the course's last ready world, which stays on screen while a newer one loads.
  const [world, setWorld] = useState<{ key: string; state: CourseWorld; ready: CourseWorld | null; course: string } | null>(null)

  useEffect(() => {
    if (!gpxUrl) return
    const set = (state: CourseWorld) =>
      setWorld((now) => ({ key, state, course: gpxUrl, ready: state.status === 'ready' ? state : now?.course === gpxUrl ? now.ready : null }))
    if (!supportsWebGL()) {
      set({ status: 'unavailable', reason: 'This browser has no WebGL, so the world can’t show.' })
      return
    }
    if (!packUrl && !browserCanBuild()) {
      // A later payload brings the pack's URL.
      if (serverStatus === 'building') set({ status: 'building', share: null })
      else set({ status: 'unavailable', reason: serverStatus === 'failed' ? 'The world could not be built.' : 'The world isn’t built yet.' })
      return
    }
    const controller = new AbortController()
    const { signal } = controller
    set({ status: 'building', share: null })
    ;(async () => {
      // The world's own chunk (three.js) comes down while the terrain is read.
      void import('./CourseMapWorld')
      const primary = fetchCourse(gpxUrl, signal)
      const [gpx, lines] = await Promise.all([
        primary,
        // A course whose line won't load is left out of the world rather than keeping it from showing.
        Promise.all(
          (others ?? []).map(async (course) => {
            if (!course.url) return null
            try {
              const line = course.url === gpxUrl ? await primary : await fetchCourse(course.url, signal)
              return { key: course.key, color: course.color, order: course.order, gpx: line }
            } catch (error) {
              if (signal.aborted) throw error
              return null
            }
          }),
        ),
      ])
      const drawn = others ? lines.filter((line): line is WorldCourse => line !== null) : null
      if (packUrl) {
        try {
          set({ status: 'ready', terrain: await fetchWorldPack(packUrl, signal), gpx, courses: drawn })
          return
        } catch (error) {
          if (signal.aborted) throw error
          // The server's pack didn't load: build it here instead, where the browser can.
          if (!browserCanBuild()) throw error
        }
      }
      const area = worldArea([gpx.bounds, ...(drawn ?? []).map((course) => course.gpx.bounds)])
      const problem = validateBox(area)
      if (problem) {
        set({
          status: 'unavailable',
          reason: problem === 'too-large' ? `The courses span more than ${MAX_SIDE_KM} km, too far for a world yet.` : 'The course can’t make a world.',
        })
        return
      }
      const { generateTerrain } = await import('@/lib/terrain-generator/generate')
      const terrain = await generateTerrain(area, {
        signal,
        onProgress: (progress) => {
          if (!signal.aborted) set({ status: 'building', share: progress.tilesRead / Math.max(1, progress.tilesTotal) })
        },
      })
      if (!signal.aborted) set({ status: 'ready', terrain, gpx, courses: drawn })
    })().catch(() => {
      if (!signal.aborted) set({ status: 'unavailable', reason: 'The world could not be built.' })
    })
    return () => controller.abort()
    // `others` is read through `key` (its URLs and colours), so a new array with the same courses doesn't rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpxUrl, packUrl, key])

  if (!gpxUrl) return { status: 'off' }
  const state: CourseWorld = world?.key === key ? world.state : { status: 'building', share: null }
  // A rebuilt pack (or a course added) loads behind the course's world on screen and swaps in once it's ready; one that
  // won't load leaves that world showing.
  if (state.status !== 'ready' && world?.course === gpxUrl && world.ready) return world.ready
  return state
}
